import React, { useEffect, useMemo, useRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSnackbar } from 'notistack';
import { z as zod } from 'zod';

import { projectsApi } from '@internal/api/projects';
import { getProjectActionErrorMessage } from '@internal/api/projects/projects';
import { isPartnerAccount } from '@internal/components/app/constants';
import { useApiContext } from '@internal/components/common/api-context';
import SidePeekDrawer from '@internal/components/profile/components/side-peek-drawer';
import useStorage from '@internal/hooks/use-storage';
import { Project } from '@internal/types/projects';
import {
  ApplicationSource,
  MEvents,
  projectApplicationMEvent,
} from '@internal/utils/mixpanel';

import ApplicationSummary from './application-summary';
import { useOptions, useQuestions } from './apply-form.hooks';
import {
  errorFieldNames,
  expectsRateExpectations,
  MAX_RATE,
  MIN_RATE,
  prepareFormData,
  requiresFullTimeAgreement,
} from './apply-form.utils';
import ApplyFormFields, { sortProjectQuestions } from './apply-form-fields';
import {
  APPLY_DRAWER_WIDTH,
  ApplyDrawerDescription,
  DrawerFormColumn,
  SubmitButton,
} from './project-view.styled';

const rateIssue = (
  ctx: zod.RefinementCtx,
  path: string,
  message: string
): void => {
  ctx.addIssue({ code: zod.ZodIssueCode.custom, path: [path], message });
};

const isValidRate = (value: string): boolean => {
  const rate = Number(value);
  return Number.isFinite(rate) && rate >= MIN_RATE && rate <= MAX_RATE;
};

const validateRateExpectations = (
  { minRate = '', maxRate = '', rateComment = '' }: RateExpectationsValues,
  ctx: zod.RefinementCtx
): void => {
  const hasRange = Boolean(minRate.trim() || maxRate.trim());
  if (!hasRange) {
    if (!rateComment.trim()) {
      rateIssue(ctx, 'rateComment', 'Enter an hourly rate range or a comment.');
    }
    return;
  }
  const minValid = isValidRate(minRate);
  const maxValid = isValidRate(maxRate);
  if (!minValid) {
    rateIssue(
      ctx,
      'minRate',
      `Enter a minimum rate between $${MIN_RATE} and $${MAX_RATE}.`
    );
  }
  if (!maxValid) {
    rateIssue(
      ctx,
      'maxRate',
      `Enter a maximum rate between $${MIN_RATE} and $${MAX_RATE}.`
    );
  }
  if (minValid && maxValid && Number(minRate) > Number(maxRate)) {
    rateIssue(
      ctx,
      'maxRate',
      'Maximum rate must be at least the minimum rate.'
    );
  }
};

const RateExpectationsObject = zod.object({
  minRate: zod.string().optional(),
  maxRate: zod.string().optional(),
  rateComment: zod
    .string()
    .max(1000, { message: 'Comment is too long' })
    .optional(),
});

type RateExpectationsValues = zod.infer<typeof RateExpectationsObject>;

const BasicDetailsObject = zod
  .object({
    preferredStartDate: zod
      .string()
      .min(1, { message: 'Enter your preferred start date' })
      .max(100),
    vacationPlans: zod
      .string()
      .min(1, { message: 'Enter your vacation plans' })
      .max(100),
    timeslots: zod
      .string()
      .min(1, { message: 'Enter your timeslots for interview' })
      .max(100),
  })
  .merge(RateExpectationsObject);

const WorkingHoursObject = zod.object({
  startTime: zod.string().min(1, { message: 'Select your start time' }),
  endTime: zod.string().min(1, { message: 'Select your finish time' }),
  timeZone: zod.string({ invalid_type_error: 'Select your timezone' }),
});

const buildSchema = (isFullTime: boolean, expectsRate: boolean) =>
  zod.object({
    basicDetails: expectsRate
      ? BasicDetailsObject.superRefine(validateRateExpectations)
      : BasicDetailsObject,
    workingHours: WorkingHoursObject,
    questions: zod.array(
      zod.object({
        id: zod.string(),
        answer: zod
          .string()
          .min(1, { message: 'Enter your answer on this question' })
          .max(1000, { message: 'Answer is too long' }),
      })
    ),
    additionalComments: zod
      .string()
      .max(1000, { message: 'Answer is too long' })
      .optional(),
    agree: isFullTime
      ? zod.literal(true, {
          errorMap: () => ({ message: 'You must agree to continue' }),
        })
      : zod.boolean().optional(),
  });

export type SchemaType = zod.infer<ReturnType<typeof buildSchema>>;

type ApplyDraftSchema = Record<string, SchemaType>;

const APPLY_DRAFT_STORAGE_KEY = 'apply-draft';

export enum ApplyDrawerMode {
  Apply = 'apply',
  Summary = 'summary',
}

const DRAWER_TITLE: Record<ApplyDrawerMode, string> = {
  [ApplyDrawerMode.Apply]: 'Apply to project',
  [ApplyDrawerMode.Summary]: 'Application summary',
};

const APPLY_INTRO =
  'Share your availability, then answer a few quick questions.';
const SUMMARY_INTRO = 'Your submitted answers. Editing is not available.';
const APPLY_INTRO_WITH_RATE =
  'Share your availability and rate, then answer a few quick questions.';
const SUBMIT_FAILED_MESSAGE =
  'The application could not be submitted. Please try again.';

const ApplyForm: React.FC<{
  open: boolean;
  mode: ApplyDrawerMode;
  coversSideColumn: boolean;
  width?: string;
  project: Project;
  onClose: () => void;
  onSubmitted: () => void;
}> = ({
  open,
  mode,
  coversSideColumn,
  width = APPLY_DRAWER_WIDTH,
  project,
  onClose,
  onSubmitted,
}) => {
  const { profile } = useApiContext();
  const { enqueueSnackbar } = useSnackbar();
  const readOnly = mode === ApplyDrawerMode.Summary;
  const expectsRate = expectsRateExpectations(
    project,
    isPartnerAccount(profile)
  );

  const sortedQuestions = useMemo(
    () => sortProjectQuestions(project.projectQuestions || []),
    [project.projectQuestions]
  );

  const { defaultValues, questionTitles } = useQuestions<SchemaType>({
    questions: sortedQuestions,
    project,
    profile,
  });

  const { timeOptions, timeZoneOptions } = useOptions();

  const { applyToProject, applyToProjectLoading } =
    projectsApi.applyToProject.useMutation();

  const isFullTime = requiresFullTimeAgreement(project);

  const formColumn = useRef<HTMLDivElement>(null);
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues,
    resolver: zodResolver(buildSchema(isFullTime, expectsRate)),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  const { fields } = useFieldArray({ control, name: 'questions' });

  const drafts = useStorage<ApplyDraftSchema>({
    domainKey: APPLY_DRAFT_STORAGE_KEY,
    type: 'session',
  });

  useEffect(() => {
    if (readOnly) return;
    const draft = drafts.getValue(project.id);
    if (!draft) return;
    const questionIds = (items: SchemaType['questions']) =>
      items.map(item => item.id).join(',');
    if (questionIds(draft.questions) === questionIds(defaultValues.questions)) {
      reset(draft, { keepDefaultValues: true });
    } else {
      drafts.removeValue(project.id);
    }
  }, [readOnly, drafts, project.id, defaultValues.questions, reset]);

  useEffect(() => {
    if (readOnly) return undefined;
    const subscription = watch(values =>
      drafts.setValue(project.id, values as SchemaType)
    );
    return () => subscription.unsubscribe();
  }, [readOnly, watch, drafts, project.id]);

  useEffect(() => {
    if (readOnly) reset(defaultValues);
  }, [readOnly, defaultValues, reset]);

  const scrollToFirstError = (errors: object) => {
    const invalid = new Set(errorFieldNames(errors));
    const anchors =
      formColumn.current?.querySelectorAll<HTMLElement>('[data-field]');
    const first = Array.from(anchors ?? []).find(anchor =>
      invalid.has(anchor.dataset.field ?? '')
    );
    first?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const submit = handleSubmit(data => {
    if (!profile) return;

    applyToProject(
      {
        ...prepareFormData({
          contactId: project.contactId,
          hubspotProjectId: project.id,
          formData: data,
          withRateExpectations: expectsRate,
        }),
        apiKey: profile.apiKey,
        dealId: project.dealId,
      },
      {
        onSuccess: () => {
          drafts.removeValue(project.id);
          projectApplicationMEvent(
            MEvents.ProjectApplicationSubmitted,
            project.dealId,
            project.offerStatus
              ? ApplicationSource.Offer
              : ApplicationSource.Self
          );
          onSubmitted();
        },
        onError: error => {
          enqueueSnackbar({
            variant: 'error',
            message: getProjectActionErrorMessage(error, SUBMIT_FAILED_MESSAGE),
          });
        },
      }
    );
  }, scrollToFirstError);

  return (
    <SidePeekDrawer
      open={open}
      title={DRAWER_TITLE[mode]}
      onClose={onClose}
      width={width}
      flat={!coversSideColumn}
      variant="persistent"
      contentBackground="grey"
      subheader={
        <ApplyDrawerDescription>
          {readOnly
            ? SUMMARY_INTRO
            : expectsRate
            ? APPLY_INTRO_WITH_RATE
            : APPLY_INTRO}
        </ApplyDrawerDescription>
      }
      footer={
        readOnly ? undefined : (
          <SubmitButton
            type="button"
            onClick={() => void submit()}
            disabled={applyToProjectLoading}
            data-testid="apply-submit"
          >
            {applyToProjectLoading ? 'Submitting' : 'Submit application'}
          </SubmitButton>
        )
      }
    >
      {readOnly ? (
        <ApplicationSummary project={project} />
      ) : (
        <DrawerFormColumn ref={formColumn}>
          <ApplyFormFields
            control={control}
            fields={fields}
            questionTitles={questionTitles}
            timeOptions={timeOptions}
            timeZoneOptions={timeZoneOptions}
            showRateExpectations={expectsRate}
            showAgreement={isFullTime}
            agreeDisabled={applyToProjectLoading}
            overlapRequirement={project.projectRequiredOverlap}
          />
        </DrawerFormColumn>
      )}
    </SidePeekDrawer>
  );
};

export default ApplyForm;
